// One Idempotency-Key per USER INTENT (API_CONTRACTS §1.2). The key is looked up by the request body,
// so a retry of the same action (after a lost response, a step-up or a double tap) reuses its key and
// the server replays the stored answer instead of creating twice.
//
// An intent ends when the server has answered it with a 2xx: the API client then calls
// `settleIdempotencyKey`, and every key store drops that key. The next identical body is a NEW
// action with a new key (a second shift the same day with the same float, a second identical cash
// sale, the same CSV imported again). The server stores only 2xx answers under a key (an error
// releases it), so keeping the key after an error or a network failure is exactly right: the retry
// either replays the answer the device never saw, or runs for the first time.
// BUG-P6E-04 / BUG-P6E-07: before this, a key lived for the whole mount, so a legitimately new action
// with the same form values replayed the previous one.
import { useEffect, useRef } from "react";

export interface IdempotencyKeys {
  /** The key for this body: the same until the server has answered it with a 2xx. */
  (body: unknown): string;
  /** Forgets every key (a new flow, e.g. "Import another file"). */
  reset(): void;
}

interface KeyStore extends IdempotencyKeys {
  /** Drops `key` (the server answered it); unknown keys are ignored. */
  settle(key: string): void;
}

const stores = new Set<KeyStore>();

/** Called by the API client after a 2xx answer to a request sent with this Idempotency-Key. */
export function settleIdempotencyKey(key: string): void {
  for (const s of stores) s.settle(key);
}

/** The key store itself (no React), exported for the unit tests. */
export function createIdempotencyKeys(newKey: () => string = () => crypto.randomUUID()): KeyStore {
  const byBody = new Map<string, string>();
  const keyFor = ((body: unknown) => {
    const k = JSON.stringify(body);
    let key = byBody.get(k);
    if (!key) {
      key = newKey();
      byBody.set(k, key);
    }
    return key;
  }) as KeyStore;
  keyFor.reset = () => byBody.clear();
  keyFor.settle = (key: string) => {
    for (const [k, v] of byBody) if (v === key) byBody.delete(k);
  };
  return keyFor;
}

/** Registers a store so `settleIdempotencyKey` reaches it; returns the unregister function. */
export function watchSettlements(store: KeyStore): () => void {
  stores.add(store);
  return () => void stores.delete(store);
}

export function useIdempotencyKeys(): IdempotencyKeys {
  const ref = useRef<KeyStore | null>(null);
  ref.current ??= createIdempotencyKeys();
  const store = ref.current;
  useEffect(() => watchSettlements(store), [store]);
  return store;
}
